import { useState, useEffect } from 'react';
import { Power, PowerOff, Zap, Check, Trash2, RefreshCw, Loader2, AlertTriangle, ChevronDown, ChevronUp, Plus, Sparkles } from 'lucide-react';
import api from '../../services/api';

const STAT_LABELS = {
  reviewCount: 'recensioni',
  visitCount: 'visite',
  menuCount: 'menu',
  dishCount: 'piatti',
  sourceCount: 'fonti',
  conversationCount: 'conversazioni'
};

export default function ServiceElementsPanel({ activityId, serviceCode }) {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null); // elementId in aggiornamento
  const [deleteTarget, setDeleteTarget] = useState(null); // { element, preview }
  const [deleting, setDeleting] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  // Addons (solo per serviceCode === 'menu')
  const [addonsRegistry, setAddonsRegistry] = useState({});
  const [addonUpdating, setAddonUpdating] = useState(null); // `${elementId}:${addonKey}`

  const fetchElements = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.request(`/admin/activities/${activityId}/services/${serviceCode}/elements`);
      setElements(res.data?.elements || []);
    } catch (err) {
      setError(err.message || 'Errore nel caricamento elementi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchElements();
  }, [activityId, serviceCode]);

  // Carica il registry addons (solo per menu) una sola volta
  useEffect(() => {
    if (serviceCode !== 'menu') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.request('/admin/services/menu/addons-registry');
        if (!cancelled) setAddonsRegistry(res.data?.registry || {});
      } catch (_) { /* non bloccante */ }
    })();
    return () => { cancelled = true; };
  }, [serviceCode]);

  const handleToggleAddon = async (element, addonKey, currentlyOn) => {
    const id = `${element.id}:${addonKey}`;
    setAddonUpdating(id);
    try {
      await api.request(`/admin/services/menu/elements/${element.id}/addons/${addonKey}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !currentlyOn })
      });
      // Aggiorna localmente senza rifare il fetch completo
      setElements(prev => prev.map(el => {
        if (el.id !== element.id) return el;
        return { ...el, addons: { ...(el.addons || {}), [addonKey]: !currentlyOn } };
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setAddonUpdating(null);
    }
  };

  const handleToggleActive = async (element) => {
    setUpdating(element.id);
    try {
      await api.request(`/admin/activities/${activityId}/services/${serviceCode}/elements/${element.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !element.isActive })
      });
      await fetchElements();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleTier = async (element) => {
    setUpdating(element.id);
    try {
      const newTier = element.tier === 'pro' ? 'free' : 'pro';
      await api.request(`/admin/activities/${activityId}/services/${serviceCode}/elements/${element.id}`, {
        method: 'PUT',
        body: JSON.stringify({ tier: newTier })
      });
      await fetchElements();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleDeletePreview = async (element) => {
    try {
      const res = await api.request(`/admin/services/${serviceCode}/elements/${element.id}/delete-preview`);
      setDeleteTarget({ element, preview: res.data });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await api.request(`/admin/activities/${activityId}/services/${serviceCode}/elements`, {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim() })
      });
      setNewName('');
      setShowCreateForm(false);
      await fetchElements();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.request(`/admin/services/${serviceCode}/elements/${deleteTarget.element.id}`, {
        method: 'DELETE'
      });
      setDeleteTarget(null);
      await fetchElements();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 border-b flex items-center justify-center gap-2 text-gray-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Caricamento elementi...
      </div>
    );
  }

  return (
    <div className="border-b">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <span>Elementi del servizio ({elements.length})</span>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setShowCreateForm(true); setExpanded(true); }}
            className="p-1 hover:bg-green-100 rounded transition-colors"
            title="Aggiungi elemento"
          >
            <Plus className="w-3.5 h-3.5 text-green-600" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); fetchElements(); }}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Aggiorna"
          >
            <RefreshCw className="w-3.5 h-3.5 text-gray-400" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {expanded && (
        <div className="px-6 pb-4 space-y-2 max-h-72 overflow-y-auto">
          {/* Create Form */}
          {showCreateForm && (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Nome nuovo elemento..."
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setNewName(''); }}
                className="p-1.5 hover:bg-gray-200 rounded transition-colors text-gray-500"
              >
                <span className="text-sm">&#x2715;</span>
              </button>
            </div>
          )}
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
              {error}
            </div>
          )}

          {elements.map((el) => (
            <div
              key={el.id}
              className="p-3 bg-gray-50 rounded-lg border border-gray-100"
            >
              <div className="flex items-center justify-between">
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900 truncate">{el.name}</span>
                  {/* Status badge */}
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                    el.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {el.isActive ? 'Attivo' : 'Inattivo'}
                  </span>
                  {/* Tier badge */}
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                    el.tier === 'pro'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {el.tier === 'pro' ? 'PRO' : 'FREE'}
                  </span>
                </div>
                {/* Stats */}
                {el.stats && Object.keys(el.stats).length > 0 && (
                  <div className="flex gap-3 mt-1">
                    {Object.entries(el.stats).map(([key, value]) => (
                      value > 0 && (
                        <span key={key} className="text-[11px] text-gray-400">
                          {value} {STAT_LABELS[key] || key}
                        </span>
                      )
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 ml-3">
                {updating === el.id ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                ) : (
                  <>
                    {/* Toggle Active (solo se il servizio supporta is_active) */}
                    {el.serviceCode !== 'page' && (
                      <button
                        onClick={() => handleToggleActive(el)}
                        className={`p-1.5 rounded transition-colors ${
                          el.isActive
                            ? 'hover:bg-red-100 text-green-600 hover:text-red-600'
                            : 'hover:bg-green-100 text-gray-400 hover:text-green-600'
                        }`}
                        title={el.isActive ? 'Disattiva' : 'Attiva'}
                      >
                        {el.isActive ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Toggle Tier */}
                    <button
                      onClick={() => handleToggleTier(el)}
                      className={`p-1.5 rounded transition-colors ${
                        el.tier === 'pro'
                          ? 'hover:bg-gray-200 text-purple-600 hover:text-gray-600'
                          : 'hover:bg-purple-100 text-gray-400 hover:text-purple-600'
                      }`}
                      title={el.tier === 'pro' ? 'Passa a FREE' : 'Passa a PRO'}
                    >
                      {el.tier === 'pro' ? <Zap className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => handleDeletePreview(el)}
                      className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              </div>

              {/* Addons premium (solo servizio menu) */}
              {serviceCode === 'menu' && Object.keys(addonsRegistry).length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase text-purple-700 mb-1.5">
                    <Sparkles className="w-3 h-3" />
                    Add-ons premium
                  </div>
                  <div className="space-y-1">
                    {Object.entries(addonsRegistry).map(([addonKey, meta]) => {
                      const isOn = !!(el.addons && el.addons[addonKey]);
                      const isLoading = addonUpdating === `${el.id}:${addonKey}`;
                      return (
                        <label
                          key={addonKey}
                          className="flex items-center gap-2 text-xs cursor-pointer select-none group"
                          title={meta.description}
                        >
                          <input
                            type="checkbox"
                            checked={isOn}
                            disabled={isLoading}
                            onChange={() => handleToggleAddon(el, addonKey, isOn)}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer disabled:opacity-50"
                          />
                          <span className="flex-1 text-gray-700 group-hover:text-gray-900">{meta.label}</span>
                          {isLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                          ) : (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${
                              isOn ? 'bg-purple-100 text-purple-700' : 'bg-gray-200 text-gray-500'
                            }`}>
                              {isOn ? 'ON' : 'OFF'}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Eliminazione definitiva</h4>
                <p className="text-sm text-gray-500">{deleteTarget.element.name}</p>
              </div>
            </div>

            {deleteTarget.preview?.warning && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 mb-4">
                {deleteTarget.preview.warning}
              </div>
            )}

            <p className="text-sm text-gray-600 mb-4">
              Questa azione non puo essere annullata.
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
              >
                {deleting ? 'Eliminazione...' : 'Elimina definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
